import { busy, h } from '../dom.ts';
import type { Api } from '../api.ts';
import {
  type AssetFolder,
  type AssetPlan,
  checkAssetSize,
  checkAssetType,
  formatBytes,
  GIF_FIRST_FRAME_NOTE,
  isGif,
  planAsset,
} from '../../../supabase/functions/_shared/core/index.ts';

export interface AssetInputOptions {
  /** Identifiant de la charte, lu au moment de l'envoi (il peut être saisi après le champ logo). */
  brandId?: () => string;
}

/**
 * Champ URL + bouton d'envoi de fichier. Appelle onChange(url, image, plan) après chargement.
 * Le fichier est préparé par le cœur (`planAsset`) : un GIF de logo part tel quel, nommé par son
 * contenu, avec ses dimensions d'affichage ; tout le reste suit le chemin existant.
 */
export function assetInput(
  api: Api,
  folder: AssetFolder,
  value: string | null,
  onChange: (url: string, img?: HTMLImageElement, plan?: AssetPlan) => void,
  opts: AssetInputOptions = {},
) {
  const url = h('input', {
    type: 'url',
    value: value ?? '',
    placeholder: 'https://…',
  });
  const file = h('input', {
    type: 'file',
    accept: 'image/png,image/jpeg,image/gif',
    hidden: true,
  });
  const btn = h('button', {
    class: 'ghost',
    type: 'button',
    onclick: () => file.click(),
  }, 'Importer…');
  const info = h('small', { class: 'muted', hidden: true });
  const measure = (u: string) => {
    const img = new Image();
    img.onload = () => onChange(u, img);
    img.onerror = () => onChange(u);
    img.src = u;
  };
  url.addEventListener('change', () => measure(url.value.trim()));
  file.addEventListener('change', () =>
    busy(btn, async () => {
      const f = file.files?.[0];
      if (!f) return;
      const bytes = new Uint8Array(await f.arrayBuffer());
      // Refus de type AVANT tout envoi, avec le type reçu : jamais un message de droits pour un problème de fichier.
      const typeError = checkAssetType(f.type, f.name, bytes);
      if (typeError) {
        info.hidden = false;
        info.className = 'warn';
        info.textContent = `${f.name} — ${formatBytes(f.size)}. ${typeError}`;
        throw new Error(typeError);
      }
      const plan = await planAsset({ bytes, type: f.type, name: f.name, folder, brandId: opts.brandId?.() });
      const check = checkAssetSize(plan, f.size);
      // Poids réel affiché dès la sélection, avertissement en couleur, refus avant tout envoi.
      const gif = isGif(bytes);
      const detail = plan.kind === 'gif'
        ? ` — GIF animé ${plan.width}×${plan.height} px, stocké tel quel (ni recompression, ni conversion), affiché en ${plan.logo_width}×${plan.logo_height} px`
        : gif
        ? ' — GIF animé, stocké tel quel (ni recompression, ni conversion, ni redimensionnement)'
        : '';
      info.hidden = false;
      info.className = check.warning || check.error ? 'warn' : 'muted';
      info.textContent = `${f.name} — ${formatBytes(f.size)}${detail}${check.warning ? `. ${check.warning}` : ''}${
        check.error ? `. ${check.error}` : ''
      }${gif && !check.error ? ` ${GIF_FIRST_FRAME_NOTE}` : ''}`;
      if (check.error) throw new Error(check.error);
      url.value = await api.uploadAsset(plan);
      if (plan.kind === 'gif') onChange(url.value, undefined, plan);
      else measure(url.value);
    }));
  return { el: h('div', null, h('div', { class: 'row' }, url, btn, file), info), input: url };
}
